/**
 * CMSPortal model
 *
 * A portal is a hosted sub-site on a DAM: DAMs can have N portals, one
 * for each subdirectory in webapps/ROOT/portals .
 *
 * @class
 */
// TODO: clean up this model
// Netx.Models.CMSPortal = Netx.Model.extend(/** @lends Netx.Models.CMSPortal */{
import tinycolor from 'tinycolor2';
import AssetModel from '@netx/core-asset/models/asset';
import {portalDefaults} from '@netx/core-cms-next/defaults';
import {getPortalNameFromUrl} from '@netx/core-cms-next/utils/getPortalNameFromUrl';
import cmsConfig from '@netx/core-cms/config';
import {DEFAULT_ATTRIBUTE_IDS} from '@netx/core-cms/services/settings/constants';
import {setStyleProperty} from '@netx/core/css';
import Base from '@netx/core/lib/netx-model';

const Models = {
	Asset: AssetModel,
};

export default class CMSPortal extends Base {
	/**
	 * @type {Netx.Rpc}
	 * @private
	 */
	get rpc() {
		return true;
	}

	/**
	 * @property {object}   defaults                         - The default attribute values
	 * @property {array}    defaults.carousel                - The default portal carousel array (assets ids)
	 * @property {number}   defaults.category                - The defualt portal category id
	 * @property {string}   defaults.color1                  - The defualt portal color1 (hex color string)
	 * @property {string}   defaults.color2                  - The defualt portal color2 (hex color string)
	 * @property {string}   defaults.copyright               - The defualt portal copyright
	 * @property {boolean}  defaults.displayCatDescriptions  - The defualt portal display category description
	 * @property {number}   defaults.font                    - The defualt portal font (asset id)
	 * @property {boolean}  defaults.googleAnalyticsEnabled  - The default portal google analytics enabled boolean
	 * @property {string}   defaults.googleAnalyticsId       - The default portal google analytics code
	 * @property {number}   defaults.group                   - The defualt portal group id
	 * @property {string}   defaults.homeRoute               - The default portal home route
	 * @property {number}   defaults.logo                    - The defualt portal logo (asset id)
	 * @property {string}   defaults.name                    - The defualt portal name (mirrors label, read only - but used to fetch portal by name... only we put the `path` in there (slug))
	 * @property {string}   defaults.path                    - The defualt portal path (slug)
	 * @property {string}   defaults.portalTitle             - The defualt portal title
	 * @property {object}   defaults.properties              - This is a clientside attribute that is populated by the server and is not writable
	 * @property {boolean}  defaults.requireLogin            - The default portal require login bool
	 * @property {string}   defaults.searchPlaceholder       - The defualt portal search placeholder text
	 * @property {string}   defaults.singleSignOnURL         - The defualt portal single sign on URL
	 * @property {string}   defaults.tagline                 - The defualt portal tagline
	 * @property {string}   defaults.termsAndConditions      - The defualt portal terms and conditions
	 * @property {number}   defaults.userLEvel               - The defualt portal user level
	 * @property {boolean}  defaults.visible                 - The defualt portal visibility
	 * @property {number}   defaults.watermark               - The defualt portal watermark (asset id)
	 * @property {string}   defaults.watermarkPos            - The defualt portal watermark position ( top-left, top-right, bottom-left, bottom-right, center )
	 */
	get defaults() {
		return {
			...portalDefaults,

			// Client attributes
			categoryModel: null,
			fontModel: null,
			groupModel: null,
			logoModel: null,
			faviconModel: null,
			properties: {},
			watermarkModel: null,
		};
	}

	/**
	 * Array of model attributes that are client specific and should never be sent to the server
	 * @type {Array}
	 */
	get clientAttrs() {
		return [
			'categoryModel',
			'fontModel',
			'groupModel',
			'logoModel',
			'faviconModel',
			'properties',
			'watermarkModel',
		];
	}

	/**
	 * @property {object} methods       - The methods available on this model
	 * @property {method} methods.read  - The read method (get)
	 */
	get methods() {
		return {
			read() {
				// TODO: note, we are actually passing the `path`(slug)
				// but name is the human-readable name of the portal (mirrors label)
				// we are not doing anything wrong with that per se... but just note
				// this call is not properly named and whatever you set as `name`,
				// before calling this, will be blown out and replaced with the human-
				// readable name
				// eslint-disable-next-line no-unreachable
				return ['getBrandPortalByName', this.get('name')];
			},
		};
	}

	/**
	 * @property {object}     parsers       - The parsers available on this model
	 * @property {function}   parsers.read  - Parses result of read method
	 */
	get parsers() {
		return {
			read(data, opts) {
				// TODO: if data is null no portal exists... server does not throw error
				// do we want to do anything this with this or ignore (before this check we just threw an error below trying to access `data.properties`)
				data = data || {};
				// DAM-9559: `properties` can occasionally be NULL for older themes.
				if (data.properties == null) {
					// don't set it.
					delete data.properties;
				}

				// TODO: this should no longer be necessary as we get this back from the custom css
				// properties endpoint
				setStyleProperty('primary', data.color1);
				setStyleProperty('secondary', data.color1);

				return Object.assign(cmsConfig, data);
			},
		};
	}

	/** @override */
	constructor(attrs, opts = {}) {
		super(attrs, opts);

		// Ideally, sub-models are handled entirely via change events,
		// avoiding the need to override set, fetch, etc.
		//
		_.each(this.subModelThings, (thing) => {
			// set/update the submodel when the associated ID changes.
			this.on(`change:${thing.name}`, (model, val, opts) => {
				// don't get the font model if on login page.
				if (!Netx.asset) return;
				this.updateSubModel(thing, val, opts);
			});
		});
	}

	/**
	 * @override
	 * @returns {object} portal without the goo
	 */
	unpackAttrs(gooeyObj) {
		if (!(this.gooAttrs && gooeyObj && gooeyObj.goo)) {
			// do nothing if model defines no goo.
			return gooeyObj;
		}

		const serverGoo = gooeyObj.goo;
		// HACK around bugs in the DAM JSON parser: unescape triple-escaped quotes!
		const clientGoo = serverGoo.replace(/\\"/g, '"');

		_.extend(cmsConfig, clientGoo);

		// with _.omit, delete the goo key
		return _.extend(
			{},
			gooeyObj,
			// with _.pick, only allow goo we've defined in .gooAttrs!
			_.pick(JSON.parse(clientGoo), this.gooAttrs),
		);
	}

	/**
	 * @type {array}
	 * @default
	 */
	// TODO: this is ugly - move to properies or idk?
	// Cannot call it subModels either as that is an array on the model itself
	get subModelThings() {
		return [
			{
				name: 'font',
				id: 'assetId',
				modelClass: 'Asset',
			},
		];
	}

	/**
	 * @method
	 * @param {object} opts Request options
	 */
	fetchPortalByName(opts = {}) {
		// TODO: note, we are actually passing the `path`(slug)
		// but name is the human-readable name of the portal (mirrors label)
		// we are not doing anything wrong with that per se... but just note
		// this call is not properly named and whatever you set as `name`,
		// before calling fetch, will be blown out and replaced with the human-
		// readable name
		return this.set('name', getPortalNameFromUrl()).fetch(opts);
	}

	/**
	 * Fetch full models for related portal objects
	 * @method
	 * @param {object} opts  Backbone options
	 * @returns {Promise} resolves when all models are fetched.
	 */
	fetchSubModels(opts = {}) {
		const promises = [];
		const subModels = {};

		_.each(this.subModelThings, (thing, idx) => {
			const m = this.get(`${thing.name}Model`);
			if (!m) return;

			m.set({
				[thing.id]: parseInt(this.get(thing.name)),
			});
			if (m.id) {
				promises.push(m.fetch(opts));
			} else {
				m.set(m.defaults);
			}
		});

		// Multiple promises
		return this.when(promises);
	}

	/**
	 * @param {object} thing definition of the subModel
	 * @param {number} subId id of the subModel's server object
	 * @param {object} opts Backbone options
	 */
	updateSubModel(thing, subId = 0, opts = {}) {
		if (!Models[thing.modelClass]) {
			throw new Error(`missing model for ${thing.name}`);
		}
		const model = new Models[thing.modelClass]({
			[thing.id]: subId,
		});
		this.set(`${thing.name}Model`, model);

		if (subId == 0) {
			// reset submodel to defaults:
			model.set(model.defaults, opts);
		} else {
			// // fetch new submodel
			// sm.fetch(opts);
			//
			//
			// DEVNOTE:
			// It would be way simpler & cleaner to fetch submodels here & entirely avoid wrapping this.fetch.
			// Trouble is, we currently don't want this.fetch to resolve until the submodels have finished fetching.
			// That's because we don't want to trigger a render until we have that data (for instance, the template needs the logo URL).
			//
			// Solutions?
			//
			// The systemBrandPortalView could be aware of the submodels & listen for their 'sync' events to trigger more renders.
			// That's not ideal, because it violates encapsulation.  (But the template already does that.)
			//
			// These subModels could trigger events on the parent model when they sync.
			// We would need some meaningful new event type for that, which feels hairy.
			//
			// This model's view could use subviews that listen to these submodels ... that also feels complicated.
			//
			// I'd rather fix this in backbone.rpc somehow -- once, for everywhere.
		}
	}

	parseAltColor() {
		const prime = this.get('color1');
		const tinyPrime = tinycolor(prime);

		const primeBrightness = tinyPrime.getBrightness();
		const lightenDarken = primeBrightness < 128;
		let primeAlt;

		if (lightenDarken) {
			primeAlt = tinyPrime.brighten(21).toHexString();
		} else {
			primeAlt = tinyPrime.darken(19).toHexString();
		}
		this.set({color2: primeAlt});
		return primeAlt;
	}

	/** @type {string} */
	static get __name__() {
		return 'CMSPortal';
	}
}
